import { api } from "../../api/apiSlice";
import { useHasPermission } from "../auth/useClaims";

// Collector customer lists + collector performance (Batch189, 2026-09-21).
//
// Which collector each customer belongs to, and how each collector did against
// that list. The rules are the user's: the first fill is automatic from
// history, EVERY customer on a list is expected EVERY calendar month, and a
// customer has ONE collector at a time.
//
// A customer is (customerBranchId, custId) and a collector is
// employeeRecordGuid - custId and empCode are only unique per branch, so never
// key a row or compare people on those alone, and never on a name.
//
// Every write changes what the report says, so every mutation invalidates both
// tags; a missed invalidation here shows as a report that disagrees with the
// list screen until someone refreshes.
export const collectorListsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCollectorOptions: builder.query({
      query: (params) => ({ url: "/collections/collectors/options", params }),
      providesTags: [{ type: "CollectorAllocation", id: "OPTIONS" }],
    }),
    getCollectorStats: builder.query({
      query: (params) => ({ url: "/collections/collectors/stats", params }),
      providesTags: [{ type: "CollectorAllocation", id: "STATS" }],
    }),
    listCollectorAllocations: builder.query({
      query: (params) => ({ url: "/collections/collectors/allocations", params }),
      providesTags: [{ type: "CollectorAllocation", id: "LIST" }],
    }),
    // preview:true writes nothing - it is a query in spirit, but it is a POST
    // and its answer must never be served from cache, so it stays a mutation
    // that simply invalidates nothing unless it applied.
    autofillCollectorAllocations: builder.mutation({
      query: (body) => ({ url: "/collections/collectors/allocations/autofill", method: "POST", data: body }),
      invalidatesTags: (result) => (result?.applied ? ["CollectorAllocation", "CollectorPerformance"] : []),
    }),
    setCollectorAllocations: builder.mutation({
      query: (body) => ({ url: "/collections/collectors/allocations", method: "PUT", data: body }),
      invalidatesTags: ["CollectorAllocation", "CollectorPerformance"],
    }),
    getCollectorPerformance: builder.query({
      query: (params) => ({ url: "/collections/collectors/performance", params }),
      providesTags: [{ type: "CollectorPerformance", id: "SUMMARY" }],
    }),
    // Batch190 - the customer NAMES behind each summary figure, one row per
    // customer + month + category.
    getCollectorPerformanceDetail: builder.query({
      query: (params) => ({ url: "/collections/collectors/performance/detail", params }),
      providesTags: [{ type: "CollectorPerformance", id: "DETAIL" }],
    }),
    getCollectorPerformanceCustomers: builder.query({
      query: (params) => ({ url: "/collections/collectors/performance/customers", params }),
      providesTags: [{ type: "CollectorPerformance", id: "CUSTOMERS" }],
    }),
    // Batch209 - collector x client x month (Jan-Dec) amounts for one year.
    getCollectorMonthlySummary: builder.query({
      query: (params) => ({ url: "/collections/collectors/monthly-summary", params }),
      providesTags: [{ type: "CollectorPerformance", id: "MONTHLY" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCollectorOptionsQuery,
  useGetCollectorStatsQuery,
  useListCollectorAllocationsQuery,
  useLazyListCollectorAllocationsQuery,
  useAutofillCollectorAllocationsMutation,
  useSetCollectorAllocationsMutation,
  useGetCollectorPerformanceQuery,
  useGetCollectorPerformanceCustomersQuery,
  useGetCollectorPerformanceDetailQuery,
  useGetCollectorMonthlySummaryQuery,
} = collectorListsApi;

// These only decide whether to SHOW a control - the API is the authority.
export const useCanManageCollectorAllocation = () => useHasPermission("Collections.ManageAllocation");
export const useCanViewCollectorPerformance = () => useHasPermission("Collections.ViewPerformance");

// ---- shared formatting ----
export const money = (n) =>
  Number(n || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Employee and customer names arrive with runs of spaces from the legacy tables.
export const tidy = (s) => (s || "").replace(/\s+/g, " ").trim();

export const customerKey = (r) => `${r.customerBranchId}-${r.custId}`;

// First name only, the same rule the API applies to every *ByName field
// (ActorNameJson): first word, skipping a leading Mr/Mrs/Ms/Miss/Dr. The
// collector name on the allocation rows is a plain "collectorName", so it
// arrives in full and is shortened here for the dashboard.
const TITLES = new Set(["MR", "MRS", "MS", "MISS", "DR"]);
export const firstName = (fullName) => {
  const parts = tidy(fullName).split(" ").filter(Boolean);
  let i = 0;
  while (i < parts.length - 1 && TITLES.has(parts[i].replace(/\./g, "").toUpperCase())) i++;
  return parts[i] || "";
};

// Anyone who may read the collector lists (the API's own read policy on
// /collections/collectors/allocations) - the dashboard only asks for the
// lists when the caller can actually get them.
export const useCanReadCollectorLists = () => {
  const manage = useHasPermission("Collections.ManageAllocation");
  const performance = useHasPermission("Collections.ViewPerformance");
  return manage || performance;
};

// "How long since anyone collected from this customer", in the words the
// report uses everywhere. null = never, in our records.
export const sinceLabel = (months) =>
  months == null ? "never" : months <= 0 ? "this month" : `${months} month${months === 1 ? "" : "s"}`;
